import type { ReactElement } from "react";
import { NavLink } from "react-router-dom";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";

interface NavItemProps {
  /** Route to navigate to. Omit for an action entry (pass `onSelect` instead). */
  to?: string;
  label: string;
  icon: ReactElement;
  /** Whether a nested route under `to` (e.g. /documents/submit) also counts as active. */
  matchNested?: boolean;
  /** Icon-only, with the label as a tooltip. For a collapsed sidebar. */
  collapsed?: boolean;
  /** Called after navigation, e.g. to close a Drawer. */
  onClick?: () => void;
  /** Action mode: render a plain button that runs this instead of navigating. Used when there is
   *  no route (e.g. Sign out). Ignored when `to` is set; never active-highlighted. */
  onSelect?: () => void;
}

const NavButton = styled(ListItemButton)<{ $collapsed?: boolean }>(({ theme, $collapsed }) => ({
  flexGrow: 0,
  flexShrink: 0,
  borderRadius: `${1.5 * theme.shape.borderRadius}px`,
  gap: theme.spacing(1),
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  minHeight: 0,
  justifyContent: $collapsed ? "center" : "flex-start",
  color: theme.palette.text.primary,
  "&.active": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  },
}));

const NavIcon = styled(ListItemIcon)({
  minWidth: 0,
  color: "inherit",
  "& svg": { fontSize: 20 },
});

/**
 * A single sidebar entry: icon + label. With `to` it is a NavLink, active-highlighted on match;
 * with `onSelect` (no `to`) it is an action button. Collapses to an icon-only button with a
 * tooltip when the sidebar is collapsed.
 */
export function NavItem({
  to,
  label,
  icon,
  matchNested = true,
  collapsed = false,
  onClick,
  onSelect,
}: NavItemProps) {
  const inner = (
    <>
      <NavIcon>{icon}</NavIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          slotProps={{ primary: { variant: "body2", fontWeight: 600 } }}
        />
      )}
    </>
  );

  const button = to ? (
    <NavButton
      component={NavLink}
      to={to}
      end={!matchNested}
      onClick={onClick}
      $collapsed={collapsed}
    >
      {inner}
    </NavButton>
  ) : (
    <NavButton
      onClick={() => {
        onSelect?.();
        onClick?.();
      }}
      $collapsed={collapsed}
    >
      {inner}
    </NavButton>
  );

  return collapsed ? (
    <Tooltip title={label} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
}
